import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FiDownload, FiShare2, FiX } from "react-icons/fi";
import { Document, Page } from "react-pdf";
import { ASSET_URI } from "../lib/utils";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

const canPreview = (mimeType) => ["application/pdf", "image/jpeg", "image/png", "image/webp"].includes(mimeType);

function CertificateFilePreviewModal({ file, onClose, onShare, t }) {
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);
  const [pageCount, setPageCount] = useState(0);
  const [pdfError, setPdfError] = useState(false);

  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    closeButtonRef.current?.focus();
    const closeOnEscape = (event) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("keydown", closeOnEscape);
      previousFocusRef.current?.focus?.();
    };
  }, [onClose]);

  useEffect(() => {
    setPageCount(0);
    setPdfError(false);
  }, [file?.previewUrl]);

  if (!file) return null;
  const previewable = canPreview(file.mimeType);
  const image = file.mimeType?.startsWith("image/");
  const previewUrl = `${ASSET_URI}${file.previewUrl}`;
  const isPdf = file.mimeType === "application/pdf";
  const pdfWidth = typeof window === "undefined" ? 960 : Math.min(window.innerWidth - 48, 960);
  return <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/70 p-3 sm:p-6" role="presentation" onMouseDown={onClose}>
    <section role="dialog" aria-modal="true" aria-labelledby="certificate-preview-title" className="flex max-h-full w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
      <header className="flex items-center justify-between gap-3 border-b px-4 py-3 sm:px-6"><div className="min-w-0"><h2 id="certificate-preview-title" className="truncate text-base font-bold text-slate-900">{file.name}</h2><p className="mt-1 text-xs uppercase tracking-wide text-slate-500">{file.mimeType || t("certificateVerification.file")}</p></div><button ref={closeButtonRef} type="button" onClick={onClose} className="rounded-lg p-2 text-slate-600 hover:bg-slate-100" aria-label={t("certificateVerification.closePreview")}><FiX /></button></header>
      <div className="min-h-0 flex-1 overflow-auto bg-slate-100 p-3 sm:p-5">
        {previewable && image ? <img src={previewUrl} alt={file.name} className="mx-auto max-h-[70vh] max-w-full rounded object-contain" /> : null}
        {previewable && isPdf && !pdfError ? <Document
          file={previewUrl}
          onLoadSuccess={({ numPages }) => { setPageCount(numPages); setPdfError(false); }}
          onLoadError={() => setPdfError(true)}
          onSourceError={() => setPdfError(true)}
          loading={<p role="status" className="py-10 text-center text-sm text-slate-600">{t("certificateVerification.loadingPdf", "Loading PDF…")}</p>}
          className="flex flex-col items-center gap-4"
        >{Array.from({ length: pageCount }, (_, index) => <Page key={`page_${index + 1}`} pageNumber={index + 1} width={pdfWidth} renderTextLayer renderAnnotationLayer />)}</Document> : null}
        {previewable && isPdf && pdfError ? <p role="alert" className="py-10 text-center text-sm text-red-700">{t("certificateVerification.pdfLoadError", "Unable to display this PDF. Please download the file to view it.")}</p> : null}
        {!previewable ? <div className="flex min-h-64 flex-col items-center justify-center text-center text-sm text-slate-600"><p>{t("certificateVerification.unsupportedPreview")}</p></div> : null}
      </div>
      <footer className="flex flex-wrap justify-end gap-2 border-t bg-white px-4 py-3 sm:px-6"><button type="button" onClick={onShare} aria-label={t("certificateVerification.shareCertificate")} title={t("certificateVerification.shareCertificate")} className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-indigo-700"><FiShare2 /></button><a href={`${ASSET_URI}${file.downloadUrl}`} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white"><FiDownload /> {t("certificateVerification.downloadCertificate")}</a></footer>
    </section>
  </div>;
}

CertificateFilePreviewModal.propTypes = { file: PropTypes.object, onClose: PropTypes.func.isRequired, onShare: PropTypes.func.isRequired, t: PropTypes.func.isRequired };
CertificateFilePreviewModal.defaultProps = { file: null };

export default CertificateFilePreviewModal;
